import type { TagProps } from '@ditioas/web-ui';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import * as Yup from 'yup';

import type { DefaultFormFile } from 'components/ReactHookForm/DNDFileInput/useHandleFileUpload';
import config from 'config/site.config';
import { useCreateAlert, useUpdateAlert } from 'data/alerts';
import { getEnumNumericKeys } from 'libs/utils';
import type {
  AlertCustomProperty,
  CompanyAlert,
  ProjectAlertType,
} from 'model/Alerts/types';
import {
  AlertStatus,
  CompanyAlertBaseType,
  CustomPropertyType,
  InjuryType,
  SeverityLevel,
} from 'model/Alerts/types';
import { uploadFile } from 'services/file-service';

import { CustomPropertyRequired } from '../AlertTypes/AlertTypeForm/utils';
import { alertsEvents, trackAlertsEvent } from '../analyticsUtils';

const {
  modules: { alerts: alertsUrls, resourcesAndMachines: machinesUrls },
} = config;

export const getAlertStatusColor = (status: AlertStatus) => {
  let color: TagProps['color'] = 'red';

  if (status === AlertStatus.InProgress) {
    color = 'azure';
  } else if (status === AlertStatus.Closed) {
    color = 'green';
  }

  return color;
};

export interface AlertFormShape {
  id?: string;
  projectId: string;
  taskId: string;
  taskName: string;
  externalId: string | null;
  duplicatedFromId?: string | null;
  title: string;
  projectNotificationTypeId: string;
  baseType: CompanyAlertBaseType;
  categoryIds: string[];
  hmsRegTypeId: string | null;
  machineId: string | null;
  description: string;
  measures: string | null;
  resolvedOnLocation: boolean;
  requiresFurtherAction: boolean;
  commentInternal: string | null;
  imageIds: (string | DefaultFormFile)[];
  fileIds: (string | DefaultFormFile)[];
  relatedToIds: string[];
  causeIds: string[];
  involvedPartiesIds: string[];
  involvedCompanyIds: string[];
  involvedResources: string | null;
  risk: SeverityLevel;
  /** InjuryType, EnvironmentalDamageType or QADamageType
  "0" for machine and OTHER alerts */
  damageType: number;
  customProperties: AlertCustomProperty[];
  caseHandlerDescriptionMeasures: string | null;
  absenceDays: number | null;
  costs: number | null;
  invoicedCost: number | null;
  status: AlertStatus;
}

export const initialAlertFormData = {
  title: '',
  projectNotificationTypeId: '',
  description: '',
  measures: '',
  commentInternal: '',
  imageIds: [],
  categoryIds: [],
  hmsRegTypeId: '',
  relatedToIds: [],
  causeIds: [],
  involvedPartiesIds: [],
  involvedResources: '',
  involvedCompanyIds: [],
  resolvedOnLocation: false,
  requiresFurtherAction: false,
  risk: SeverityLevel.Medium,
  damageType: 0,
  customProperties: [],
  absenceDays: 0,
  costs: null,
  invoicedCost: null,
  status: AlertStatus.Open,
};

export const mapAlertToForm = (alert: CompanyAlert, isDuplication = false) => {
  const mappedAlert: AlertFormShape = {
    externalId: alert.externalId || null,
    projectId: alert.projectId,
    taskId: alert.taskId,
    taskName: alert.taskName,
    title: alert.title,
    projectNotificationTypeId: alert.projectNotificationTypeId,
    baseType: alert.baseType,
    categoryIds: alert.categoryIds || [],
    hmsRegTypeId: alert.hmsRegTypeId || '',
    machineId: alert.machineId,
    description: alert.description,
    measures: alert.measures,
    resolvedOnLocation: alert.resolvedOnLocation,
    requiresFurtherAction: alert.requiresFurtherAction,
    commentInternal: alert.commentInternal || '',
    imageIds: alert.imageIds,
    fileIds: alert.fileIds,
    relatedToIds: alert.relatedToIds || [],
    causeIds: alert.causeIds || [],
    involvedPartiesIds: alert.involvedPartiesIds || [],
    involvedCompanyIds: alert.involvedCompanyIds || [],
    involvedResources: alert.involvedResources || null,
    risk: alert.risk,
    caseHandlerDescriptionMeasures: alert.caseHandlerDescriptionMeasures,
    damageType: alert.damageType || 0,
    customProperties: alert.customProperties || [],
    absenceDays: alert.absenceDays ?? null,
    costs: alert.costs ?? null,
    invoicedCost: alert.invoicedCost ?? null,
    status: alert.status,
  };

  if (!isDuplication) {
    mappedAlert.id = alert.id;
    mappedAlert.externalId = alert.externalId || null;
  } else {
    mappedAlert.duplicatedFromId = alert.id;
    mappedAlert.status = AlertStatus.Open;
  }

  return mappedAlert;
};

export const useCreateAlertValidationSchema: (
  relevantTypes: ProjectAlertType[] | undefined,
  attemptedToClose: boolean
) => Yup.Lazy<AlertFormShape> = (relevantTypes, attemptedToClose) => {
  const { t } = useTranslation();

  return Yup.lazy((value: AlertFormShape) => {
    const selectedType = relevantTypes?.find(
      (type) => type.id === value.projectNotificationTypeId
    );

    const { status } = value;
    const treatAsClosed = status === AlertStatus.Closed || attemptedToClose;

    return Yup.object().shape({
      machineId:
        selectedType?.baseType === CompanyAlertBaseType.Machine
          ? Yup.string().required(t('ValidationError.Required'))
          : Yup.string().optional().nullable().default(null),
      projectId: Yup.string().required(t('ValidationError.Required')),
      taskId: Yup.string().required(t('ValidationError.Required')),
      externalId: Yup.string().nullable().default(null),
      duplicatedFromId: Yup.string().nullable().default(null),
      title: Yup.string()
        .required(t('ValidationError.Required'))
        .isNotWhiteSpace(t('ValidationError.NotWhiteSpace')),
      projectNotificationTypeId: Yup.string().required(
        t('ValidationError.Required')
      ),
      categoryIds: Yup.array()
        .of(Yup.string().required())
        .min(
          selectedType?.categories.length && treatAsClosed ? 1 : 0,
          t('ValidationError.Required')
        )
        .typeError(t('ValidationError.Required'))
        .required()
        .default([]),
      hmsRegTypeId: Yup.string().nullable().default(null),
      description: Yup.string()
        .required(t('ValidationError.Required'))
        .isNotWhiteSpace(t('ValidationError.NotWhiteSpace')),
      measures: Yup.string()
        .isNotWhiteSpace(t('ValidationError.NotWhiteSpace'))
        .nullable()
        .default(null),
      resolvedOnLocation: Yup.boolean().default(false),
      requiresFurtherAction: Yup.boolean().default(false),
      commentInternal: Yup.string()
        .when('status', {
          is: () => treatAsClosed,
          then: () =>
            Yup.string()
              .isNotWhiteSpace(t('ValidationError.NotWhiteSpace'))
              .required(t('ValidationError.Required')),
          otherwise: () =>
            Yup.string()
              .isNotWhiteSpace(t('ValidationError.NotWhiteSpace'))
              .nullable(),
        })
        .default(null),
      imageIds: Yup.array()
        .of(
          Yup.mixed<string | DefaultFormFile>()
            .test(
              'is-string-or-object',
              'Array items must be either strings or objects',
              (image) =>
                typeof image === 'string' ||
                (typeof image === 'object' && !!(image as DefaultFormFile).file)
            )
            .defined()
        )
        .min(
          selectedType?.imageRequired ? 1 : 0,
          t('ValidationError.UploadAtLeastOneImage')
        )
        .default([]),
      fileIds: Yup.array()
        .of(
          Yup.mixed<string | DefaultFormFile>()
            .test(
              'is-string-or-object',
              'Array items must be either strings or objects',
              (image) =>
                typeof image === 'string' ||
                (typeof image === 'object' && !!(image as DefaultFormFile).file)
            )
            .defined()
        )
        .min(0, t('ValidationError.UploadAtLeastOneImage'))
        .default([]),
      relatedToIds: Yup.array()
        .of(Yup.string().required())
        .min(
          selectedType?.workRelatedTo.length && treatAsClosed ? 1 : 0,
          t('ValidationError.Required')
        )
        .typeError(t('ValidationError.Required'))
        .required(),
      causeIds: Yup.array()
        .of(Yup.string().required())
        .min(
          selectedType?.cause.length && treatAsClosed ? 1 : 0,
          t('ValidationError.Required')
        )
        .typeError(t('ValidationError.Required'))
        .required(),
      involvedPartiesIds: Yup.array()
        .of(Yup.string().required())
        .min(
          selectedType?.involvedParties.length && treatAsClosed ? 1 : 0,
          t('ValidationError.Required')
        )
        .typeError(t('ValidationError.Required'))
        .required(),
      involvedCompanyIds: Yup.array().of(Yup.string().required()).default([]),
      involvedResources: Yup.string()
        .isNotWhiteSpace(t('ValidationError.NotWhiteSpace'))
        .nullable()
        .default(null),
      risk: selectedType?.risk
        ? Yup.number()
            .oneOf(getEnumNumericKeys(SeverityLevel))
            .default(SeverityLevel.Low)
        : Yup.number().default(SeverityLevel.Low),
      caseHandlerDescriptionMeasures: Yup.string()
        .when('risk', {
          is: (risk: SeverityLevel) =>
            risk === SeverityLevel.High || risk === SeverityLevel.Critical,
          then: (schema) =>
            schema
              .isNotWhiteSpace(t('ValidationError.NotWhiteSpace'))
              .required(t('ValidationError.Required')),
          otherwise: (schema) => schema.nullable(),
        })
        .default(null),
      damageInfo: Yup.number(),
      absenceDays:
        value.baseType === CompanyAlertBaseType.HSE &&
        value.damageType === InjuryType.WithAbsence
          ? Yup.number()
              .typeError(t('ValidationError.Integer_TypeError'))
              .required(t('ValidationError.Required'))
              .min(1, t('ValidationError.Required'))
          : Yup.number()
              .typeError(t('ValidationError.Integer_TypeError'))
              .optional()
              .nullable()
              .default(null),
      costs:
        treatAsClosed && selectedType?.costRequired
          ? Yup.number()
              .transform((value, original) =>
                original === '' || original === null || original === undefined
                  ? null
                  : value
              )
              .typeError(t('ValidationError.Integer_TypeError'))
              .nullable()
              .required(t('ValidationError.Required'))
          : Yup.number()
              .transform((value, original) =>
                original === '' || original === null || original === undefined
                  ? null
                  : value
              )
              .typeError(t('ValidationError.Integer_TypeError'))
              .optional()
              .nullable()
              .default(null),
      invoicedCost:
        treatAsClosed && selectedType?.costRequired
          ? Yup.number()
              .transform((value, original) =>
                original === '' || original === null || original === undefined
                  ? null
                  : value
              )
              .typeError(t('ValidationError.Integer_TypeError'))
              .nullable()
              .required(t('ValidationError.Required'))
          : Yup.number()
              .transform((value, original) =>
                original === '' || original === null || original === undefined
                  ? null
                  : value
              )
              .typeError(t('ValidationError.Integer_TypeError'))
              .optional()
              .nullable()
              .default(null),
      /* fields that do not require actual validation but are a part of the form */
      id: Yup.string().optional(),
      taskName: Yup.string().optional().default(''),
      baseType: Yup.number().default(CompanyAlertBaseType.HSE),
      damageType: Yup.number().default(0),
      customProperties: Yup.array()
        .of(
          Yup.object().shape({
            propertyId: Yup.string().required(),
            propertyName: Yup.string().default(''),
            datasetItemName: Yup.string().nullable().defined(),
            dropdownOptions: Yup.array()
              .of(
                Yup.object().shape({
                  id: Yup.string().required(),
                  name: Yup.string().required(),
                })
              )
              .defined(),
            textValue: Yup.string()
              .when('status', {
                is: () => treatAsClosed,
                then: (schema) =>
                  schema.when('propertyId', {
                    is: (propertyId: string) => {
                      const property = selectedType?.customProperties.find(
                        (prop) => prop.id === propertyId
                      );

                      return (
                        property?.type === CustomPropertyType.Text &&
                        property?.required !== CustomPropertyRequired.Optional
                      );
                    },
                    then: (schma) =>
                      schma
                        .required(t('ValidationError.Required'))
                        .isNotWhiteSpace(t('ValidationError.Required')),
                    otherwise: (schma) => schma.nullable(),
                  }),
                otherwise: (schema) => schema.nullable(),
              })
              .default(null),
            dropdownIds: Yup.array()
              .of(Yup.string().defined())
              .when('status', {
                is: () => treatAsClosed,
                then: (schema) =>
                  schema.when('propertyId', {
                    is: (propertyId: string) => {
                      const property = selectedType?.customProperties.find(
                        (prop) => prop.id === propertyId
                      );

                      return (
                        property?.type === CustomPropertyType.Dropdown &&
                        property?.required !== CustomPropertyRequired.Optional
                      );
                    },
                    then: (schma) =>
                      schma
                        .min(1, t('ValidationError.PickAtLeastOneOption'))
                        .required(t('ValidationError.Required')),
                    otherwise: (schma) => schma.nullable(),
                  }),
                otherwise: (schema) => schema.nullable(),
              })
              .default(null),
            datasetItemId: Yup.string()
              .when('status', {
                is: () => treatAsClosed,
                then: (schema) =>
                  schema.when('propertyId', {
                    is: (propertyId: string) => {
                      const property = selectedType?.customProperties.find(
                        (prop) => prop.id === propertyId
                      );

                      return (
                        property?.type === CustomPropertyType.Dataset &&
                        property?.required !== CustomPropertyRequired.Optional
                      );
                    },
                    then: (schma) =>
                      schma
                        .required(t('ValidationError.Required'))
                        .isNotWhiteSpace(t('ValidationError.Required')),
                    otherwise: (schma) => schma.nullable(),
                  }),
                otherwise: (schema) => schema.nullable(),
              })
              .default(null),
            datasetItemExternalId: Yup.string().nullable().default(null),
            datasetItemNumber: Yup.string().nullable().default(null),
            datasetData: Yup.object().nullable().default(null),
          })
        )
        .default([]),
      status: Yup.number().default(0),
    });
  });
};

const uploadAndReturnNewFileIds = async (
  files: (string | DefaultFormFile)[],
  idRef: string | undefined
) => {
  const newFiles: Blob[] = [];
  const existingFileIds: string[] = [];

  (files as (string | DefaultFormFile)[])?.forEach((file) =>
    typeof file === 'string'
      ? existingFileIds.push(file)
      : newFiles.push(file.file)
  );

  const resultFileIds = existingFileIds || [];

  if (newFiles.length) {
    const response = await uploadFile({
      file: newFiles.length === 1 ? newFiles[0] : newFiles,
      params: {
        accessType: 'internal',
        idRef,
        collectionRef: 'ProjectNotificationV2',
      },
    });
    response.files.forEach((file) => resultFileIds.push(file.fileReference.id));
  }

  return resultFileIds;
};

export const useOnSubmitAlert = () => {
  const createAlert = useCreateAlert();
  const updateAlert = useUpdateAlert();

  const { pathname, search } = useLocation();
  const navigate = useNavigate();

  return async (
    values: AlertFormShape,
    selectedType: ProjectAlertType,
    isNewAlert: boolean,
    alert: CompanyAlert | undefined,
    overrideBaseType: boolean,
    resultIsMachineAlert: boolean
  ) => {
    const imageIds = await uploadAndReturnNewFileIds(
      values.imageIds as (string | DefaultFormFile)[],
      values.id
    );

    const fileIds = await uploadAndReturnNewFileIds(
      values.fileIds as (string | DefaultFormFile)[],
      values.id
    );

    const requiresRisk = selectedType.risk;
    const requiresDamageInfo = selectedType.damageInfo;

    const newAlert = {
      ...alert,
      ...values,
      risk: requiresRisk ? values.risk : undefined,
      caseHandlerDescriptionMeasures:
        requiresRisk &&
        (values.risk === SeverityLevel.High ||
          values.risk === SeverityLevel.Critical)
          ? values.caseHandlerDescriptionMeasures
          : null,
      damageType: requiresDamageInfo ? values.damageType : undefined,
      imageIds,
      fileIds,
    };

    if (isNewAlert) {
      const createdAlert = await createAlert(newAlert);
      if (createdAlert) {
        navigate(
          {
            pathname: pathname.replace('new', createdAlert.id),
            search,
          },
          { replace: true }
        );
      }
    } else {
      const updatedAlert = await updateAlert(newAlert as CompanyAlert);
      if (alert?.baseType !== newAlert.baseType) {
        trackAlertsEvent(alertsEvents.UPDATE_TYPE);
      }

      if (overrideBaseType) {
        if (resultIsMachineAlert) {
          navigate(`${machinesUrls.alertsUrl}/${updatedAlert?.id}`);
        } else {
          navigate(
            `${alertsUrls.listUrl}/${updatedAlert?.id}?projectId=${updatedAlert?.projectId}&projectName=${updatedAlert?.projectName}`
          );
        }
      }
    }
  };
};
